import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/common/Layout';
import { SEOHead } from '../components/seo/SEOHead';
import { blogService } from '../services/firebase/blog';
import { BlogPost } from '../types/blog';

const formatDate = (date?: Date | null) => {
  if (!date) {
    return 'Unscheduled';
  }

  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(date);
};

export const BlogPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get('tag') || '';

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const loaded = await blogService.getPublicPosts(50);
        setPosts(loaded);
      } catch (err) {
        console.error('Failed to load blog posts:', err);
        setError('Unable to load blog articles right now. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  useEffect(() => {
    if (loading) return;
    if (typeof window !== 'undefined') {
      (window as any).__PRERENDER_READY__ = true;
    }
  }, [loading]);

  const tags = useMemo(() => {
    const values = new Set<string>();
    posts.forEach((post) => {
      post.tags.forEach((tag) => values.add(tag));
    });

    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [posts]);

  const filteredPosts = useMemo(() => {
    if (!activeTag) {
      return posts;
    }

    return posts.filter((post) => post.tags.includes(activeTag));
  }, [posts, activeTag]);

  return (
    <Layout>
      <SEOHead
        title="Blog"
        description="Practical AI learning guidance, insights, and product updates from trAInedup.ai."
        canonical="https://trainedup.ai/blog"
        ogType="website"
      />

      <section className="max-w-6xl mx-auto px-4 py-10">
        <header className="mb-8">
          <h1 className="font-montserrat text-4xl font-bold text-neutral-900">trAInedup Blog</h1>
          <p className="mt-3 text-neutral-600 max-w-3xl">
            Hands-on AI tips, learning strategy, and updates from the team.
          </p>
        </header>

        {tags.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className={`rounded-full border px-3 py-1 text-sm ${
                !activeTag ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-700'
              }`}
            >
              All
            </button>
            {tags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setSearchParams({ tag })}
                className={`rounded-full border px-3 py-1 text-sm ${
                  activeTag === tag ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-700'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {loading && <p className="text-neutral-600">Loading blog posts...</p>}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && filteredPosts.length === 0 && (
          <p className="text-neutral-600">No posts found for this filter yet.</p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredPosts.map((post) => (
            <article key={post.id} className="rounded-2xl border border-neutral-200 overflow-hidden bg-white">
              {post.coverImageUrl && (
                <img src={post.coverImageUrl} alt={post.title} className="h-52 w-full object-cover" loading="lazy" />
              )}
              <div className="p-6">
                <p className="text-sm text-neutral-500">{formatDate(post.publishAt || post.publishedAt)}</p>
                <h2 className="mt-2 text-2xl font-semibold text-neutral-900">{post.title}</h2>
                <p className="mt-3 text-neutral-700">{post.excerpt}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span key={`${post.id}-${tag}`} className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs text-neutral-700">
                      {tag}
                    </span>
                  ))}
                </div>
                <Link
                  to={`/blog/${post.slug}`}
                  className="mt-6 inline-flex items-center font-semibold text-primary hover:underline"
                >
                  Read article
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>
    </Layout>
  );
};
